import type { JsonValue } from '../../../../types/json'

/**
 * Parent-group child-id cleanup helper.
 *
 * When a grouped child node is removed (user delete, programmatic
 * removeNodeFromYDoc, etc.) the parent group's ``in_child_node_ids``
 * array must be updated so the slot doesn't outlive the child. Without
 * this, deleting a clip from a video_group leaves a stale id pointer
 * that causes ghost slots until the next FE rebuild.
 *
 * Logic ported from v1 ``WorkspacePageV2.tsx onNodesChange`` (lines
 * 1906-1949). Extracted here so v2's Canvas wrapper can import it
 * without duplicating the rules, and so the unit test runs against
 * the same code both call sites use.
 *
 * Pure function — no React, no ReactFlow imports. Caller passes the
 * minimum needed: the ids being removed plus a snapshot of the current
 * nodes array (id, type, parentId, data.in_child_node_ids only).
 */

export type ParentCleanupNodeShape = {
  id: string
  type?: string
  parentId?: string
  data?: { in_child_node_ids?: JsonValue }
}

/**
 * For each removed grouped child, compute the parent group's updated
 * ``in_child_node_ids`` (slot nulled, trailing nulls trimmed).
 *
 * Returns an empty Map when no removed node has a parent group — the
 * caller can short-circuit without iterating.
 *
 * Only parents of ``type === 'nodeGroup'`` participate; other parent
 * types (e.g. webtoonGroup) preserve their own membership rules.
 */
export function computeParentGroupCleanup(
  removedIds: ReadonlyArray<string>,
  currentNodes: ReadonlyArray<ParentCleanupNodeShape>,
): Map<string, (string | null)[]> {
  const updates = new Map<string, (string | null)[]>()
  if (removedIds.length === 0) return updates

  for (const removedId of removedIds) {
    const removedNode = currentNodes.find((n) => n.id === removedId)
    const parentId = removedNode?.parentId
    if (!parentId) continue
    const parentGroup = currentNodes.find((n) => n.id === parentId)
    if (!parentGroup || parentGroup.type !== 'nodeGroup') continue
    const rawChildIds = parentGroup.data?.in_child_node_ids
    if (!Array.isArray(rawChildIds)) continue

    // ``rawChildIds`` may already be the cleaned-up version from a
    // previous iteration in this same batch (multiple children of the
    // same parent removed together). Pick that up so accumulated nulls
    // and trims don't clobber each other.
    // YDoc arrays are untyped, so validate each slot into the shape this
    // function models (a child id, or null for a vacated slot) instead of
    // asserting the array's element type.
    const typedChildIds: (string | null)[] = rawChildIds.map((cid) =>
      typeof cid === 'string' ? cid : null,
    )
    const baseline: (string | null)[] = updates.get(parentId) ?? typedChildIds
    let childIds = baseline.map((cid) => (cid === removedId ? null : cid))
    while (childIds.length > 0 && childIds[childIds.length - 1] === null) {
      childIds = childIds.slice(0, -1)
    }
    updates.set(parentId, childIds)
  }

  return updates
}
